import React from 'react';
import './ParticipantGroupPriceMatrix.css';
import Togglebox from '../input/Togglebox';
import ParticipantGroupDot from './ParticipantGroupDot';
import { parsePriceInput, samePrice, toPriceInput } from '../../util/eventPrices';

/**
 * One row per group: Eintritt (· Freigegeben). Table on wide containers, one card per group on narrow ones
 * (container query — works the same inside popups and pages). Add-on prices (e.g. Leihausrüstung) are not part
 * of it — they live per process (section "Zusätze").
 *
 * rows: { [groupId]: { entry: string, released?: bool, materialized?: bool } }
 *       Prices are input strings (see util/eventPrices toPriceInput/parsePriceInput).
 * onChange(groupId, patch)
 * showRelease: release switch per group; releaseLockedReason(group) → text when the switch is locked.
 * inherit: template editor mode — an empty field inherits; inheritedValues(group) → { entry } (number) shown as
 *          placeholder.
 * Virtual rows (materialized === false) carry the chip "Standard" — saving creates them.
 */
function ParticipantGroupPriceMatrix({
  groups, rows, onChange, showRelease = false, releaseLockedReason,
  inherit = false, inheritedValues, disabled = false, ariaLabel = 'Preise je Gruppe',
}) {
  const list = groups ?? [];
  const cols = showRelease ? 'withRelease' : 'entryOnly';

  return (
    <div className="participantGroupPriceMatrix" role="table" aria-label={ariaLabel}>
      <div className={`pgpmRow pgpmHead ${cols}`} role="row">
        <span role="columnheader">Gruppe</span>
        <span role="columnheader" className="pgpmNum">Eintritt (€)</span>
        {showRelease && <span role="columnheader" className="pgpmRelease">Freigegeben</span>}
      </div>
      {list.map(g => {
        const row = rows?.[g.id] ?? { entry: '', released: false };
        const inherited = inherit && inheritedValues ? inheritedValues(g) : null;
        const lockedReason = showRelease && releaseLockedReason ? releaseLockedReason(g) : null;
        const isVirtual = row.materialized === false;
        return (
          <div key={g.id} className={`pgpmRow ${cols}`} role="row">
            <span className="pgpmGroup" role="cell">
              <ParticipantGroupDot participantGroup={g} withTooltip={false} />
              <span className="pgpmGroupName">{g.name}</span>
              {isVirtual && <span className="pgpmChip" title="Noch nicht gespeichert – Werte aus Gruppe bzw. Vorlage">Standard</span>}
            </span>
            <label className="pgpmField" role="cell">
              <span className="pgpmFieldLabel">Eintritt (€)</span>
              <input
                type="number" min={0} max={10000} step={0.01} inputMode="decimal"
                required={!inherit}
                disabled={disabled}
                value={row.entry ?? ''}
                placeholder={inherited ? toPriceInput(inherited.entry) : ''}
                aria-label={`Eintritt ${g.name}`}
                onChange={e => onChange?.(g.id, { entry: e.target.value })}
              />
            </label>
            {showRelease && (
              <span className="pgpmRelease" role="cell" title={lockedReason ?? undefined}>
                <span className="pgpmFieldLabel">Freigegeben</span>
                <span className={`pgpmToggle${lockedReason ? ' isLocked' : ''}`}>
                  <Togglebox
                    checked={!!row.released}
                    onChange={e => { if (!lockedReason && !disabled) onChange?.(g.id, { released: e.target.checked }); }}
                  />
                </span>
                {lockedReason && <span className="pgpmLockedHint">{lockedReason}</span>}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// EventParticipantGroupSettingDto[] → matrix rows (input strings).
export function settingsToMatrixRows(settings) {
  const rows = {};
  (Array.isArray(settings) ? settings : []).forEach(s => {
    rows[Number(s.participantGroupId)] = {
      entry: toPriceInput(s.entryPrice),
      released: !!s.released,
      materialized: s.materialized !== false,
      releaseSource: s.releaseSource ?? null,
    };
  });
  return rows;
}

// Validation of required price rows: returns an error text or ''.
export function validateMatrixRows(rows, groups) {
  for (const g of groups ?? []) {
    const entry = parsePriceInput(rows?.[g.id]?.entry);
    if (entry == null || Number.isNaN(entry)) return `Bitte Eintritt für „${g.name}“ angeben (0 oder mehr).`;
  }
  return '';
}

// Changed rows only → ParticipantGroupSettingInput[] (null = unchanged). `original` = rows as loaded.
// Virtual rows count as changed when anything differs or `includeVirtual` is set (saving materializes them).
export function matrixRowsToInputs(rows, original, { showRelease = false, includeVirtual = false } = {}) {
  const inputs = [];
  Object.keys(rows ?? {}).forEach(key => {
    const id = Number(key);
    const r = rows[key];
    const o = original?.[key] ?? {};
    const entry = parsePriceInput(r.entry);
    const input = { participantGroupId: id };
    let changed = includeVirtual && o.materialized === false;
    if (entry != null && !Number.isNaN(entry) && !samePrice(entry, parsePriceInput(o.entry))) { input.entryPrice = entry; changed = true; }
    if (showRelease && !!r.released !== !!o.released) { input.released = !!r.released; changed = true; }
    if (changed) {
      if (input.entryPrice == null && entry != null && !Number.isNaN(entry)) input.entryPrice = entry;
      inputs.push(input);
    }
  });
  return inputs;
}

export default ParticipantGroupPriceMatrix;
